import React, { useState, useRef, useEffect, useCallback } from 'react';

interface InlineEditableDateProps {
  value: string; // ISO date string or empty
  onSave: (newValue: string) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
}

/**
 * InlineEditableDate
 * 
 * A date display that becomes a date picker when clicked.
 * - Click on date → date picker appears
 * - Select date / blur → save
 * - ESC → cancel
 */
const InlineEditableDate: React.FC<InlineEditableDateProps> = ({
  value,
  onSave,
  placeholder = 'Geen datum',
  className = '',
  disabled = false,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [tempValue, setTempValue] = useState(value);
  const inputRef = useRef<HTMLInputElement>(null);

  // Sync tempValue when value prop changes
  useEffect(() => {
    if (!isEditing) {
      setTempValue(value);
    }
  }, [value, isEditing]);

  // Focus and open picker when entering edit mode
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      // Try to open the date picker
      try {
        inputRef.current.showPicker?.();
      } catch {
        // showPicker not supported in all browsers
      }
    }
  }, [isEditing]);

  const handleStartEdit = useCallback(() => {
    if (disabled) return;
    setTempValue(value);
    setIsEditing(true);
  }, [disabled, value]);

  const handleSave = useCallback(() => {
    if (tempValue !== value) {
      onSave(tempValue);
    }
    setIsEditing(false);
  }, [tempValue, value, onSave]);

  const handleCancel = useCallback(() => {
    setTempValue(value);
    setIsEditing(false);
  }, [value]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSave();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      handleCancel();
    }
  }, [handleSave, handleCancel]);

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setTempValue(e.target.value);
    // Auto-save on date selection
    if (e.target.value) {
      onSave(e.target.value);
      setIsEditing(false);
    }
  }, [onSave]);

  const handleBlur = useCallback(() => {
    // Small delay to allow date picker selection
    setTimeout(() => {
      handleSave();
    }, 100);
  }, [handleSave]);

  // Format date for display
  const formatDate = (dateStr: string): string => {
    if (!dateStr) return placeholder;
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('nl-NL', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      });
    } catch {
      return dateStr;
    }
  };

  // Edit mode
  if (isEditing) {
    return (
      <input
        ref={inputRef}
        type="date"
        value={tempValue}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        className={`
          px-2 py-1 text-xs
          border border-orange-400 rounded-md
          focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent
          bg-white
        `.trim()}
      />
    );
  }

  // Read mode
  const displayValue = formatDate(value);
  const isEmpty = !value;

  return (
    <span
      onClick={handleStartEdit}
      className={`
        cursor-pointer
        hover:bg-gray-50 hover:rounded px-1
        transition-colors duration-150
        ${isEmpty ? 'text-gray-400 italic' : 'text-gray-500'}
        ${disabled ? 'cursor-default hover:bg-transparent' : ''}
        ${className}
      `.trim()}
      title={disabled ? undefined : 'Klik om datum te wijzigen'}
    >
      {displayValue}
    </span>
  );
};

export default InlineEditableDate;
